import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/CombinedAuthContext";
import { recordHit } from "../utils/siteBeacon";

/**
 * SiteBeacon — fires one visitor/session hit per route change, and one extra
 * hit whenever auth state flips (so an anonymous session becomes associated
 * with the user right after login). Renders nothing.
 *
 * Mounted once inside the Router in App.js. Deliberately consent-independent
 * (security basis) — see src/utils/siteBeacon.js.
 */
export default function SiteBeacon({ onBlocked }) {
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const lastKey = useRef(null);

  useEffect(() => {
    const path = `${location.pathname}${location.search || ""}`;
    // Key on path + auth state so a login/logout with no navigation still
    // fires a hit (the login-association beacon), while ordinary re-renders
    // on the same route+auth do not.
    const key = `${path}|${isAuthenticated ? "auth" : "anon"}`;
    if (key === lastKey.current) return;
    lastKey.current = key;
    recordHit(path).then((res) => {
      if (res && res.blocked && typeof onBlocked === "function") onBlocked();
    });
  }, [location.pathname, location.search, isAuthenticated, onBlocked]);

  return null;
}
